import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { SubmitButton } from "../../components/Input";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { API_URL } from "../../config";
import { showToast } from "../../components/toast";
import LoadingSpinner from "../../components/LoadingSpinner";

const EditCustomPage = () => {
  const [title, setTitle] = useState("");
  const [heading, setHeading] = useState("");
  const [shortDesc, setShortDesc] = useState("");
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(true);
  const { id } = useParams();
  const navigate = useNavigate();

  // Fetch the page details when component mounts
  useEffect(() => {
    if (id) {
      fetchPage(id);
    }
  }, [id]);

  const fetchPage = async (id) => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      // Updated API endpoint to use the correct URL
      const response = await fetch(`${API_URL}CustomPage/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`API responded with status: ${response.status}`);
      }

      const data = await response.json();
      console.log("Fetched page data:", data);

      // Check if data is directly in the response or nested under data property
      if (data.success && data.data) {
        // Data is nested under data property
        setTitle(data.data.title || "");
        setHeading(data.data.heading || "");
        setShortDesc(data.data.short_desc || "");
        setDescription(data.data.description || "");
      } else if (data.id) {
        // Data is directly in the response
        setTitle(data.title || "");
        setHeading(data.heading || "");
        setShortDesc(data.short_desc || "");
        setDescription(data.description || "");
      } else {
        showToast.error(
          "Failed to fetch page details: Invalid response format"
        );
      }
    } catch (err) {
      console.error("Error fetching page details:", err);
      showToast.error(`Failed to fetch page details: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleEditorChange = (value) => {
    setDescription(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const formData = {
      id,
      title,
      heading,
      short_desc: shortDesc,
      description,
    };

    try {
      const token = localStorage.getItem("authToken");
      const url = `${API_URL}CustomPage/${id}`;

      console.log("Submitting update data:", formData);

      const response = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("API Error Response:", errorText);
        throw new Error(`API responded with status: ${response.status}`);
      }

      const result = await response.json();
      console.log("API Response:", result);

      if (response.status === 200 || result.success) {
        showToast.success("Page Updated Successfully");
        navigate("/CustomPages");
      } else {
        showToast.error(result.message || "Failed to update the page");
      }
    } catch (err) {
      console.error("Error updating page:", err);
      showToast.error(`Failed to update the page: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <LoadingSpinner />
    );
  }

  return (
    <div className="page-container mt-5">
      <div className="">
        <div className="panel panel-default with-rounded-corner with-shadow">
          <form onSubmit={handleSubmit}>
            <input type="hidden" name="id" value={id} />
            <div className="panel-heading border-0">
              <span className="panel-title">Edit Custom Page</span>
            </div>
            <div className="panel-body">
              <div className="row">
                {/* Title */}
                <div className="col-md-4">
                  <label>Title</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter Title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    required
                  />
                </div>

                {/* Heading */}
                <div className="col-md-4">
                  <label>Heading</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter Heading"
                    value={heading}
                    onChange={(e) => setHeading(e.target.value)}
                  />
                </div>

                {/* Short Description */}
                <div className="col-md-4">
                  <label>Short Description</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter Short Description"
                    value={shortDesc}
                    onChange={(e) => setShortDesc(e.target.value)}
                  />
                </div>

                {/* Description with ReactQuill */}
                <div className="col-md-12 mt-3">
                  <label>Description</label>
                  <ReactQuill
                    value={description}
                    onChange={handleEditorChange}
                    modules={{
                      toolbar: [
                        [{ header: "1" }, { header: "2" }, { font: [] }],
                        [{ list: "ordered" }, { list: "bullet" }],
                        ["bold", "italic", "underline"],
                        ["link", "image"],
                        ["blockquote", "code-block"],
                        [{ align: [] }],
                        ["clean"],
                      ],
                    }}
                    style={{ height: "300px", marginBottom: "50px" }}
                  />
                </div>
              </div>

              <SubmitButton
                pageBack={`/CustomPages`}
                handleSubmit={handleSubmit}
                loading={loading}
              />
            </div>
          </form>
        </div>
      </div>
      {/* Scroll to top button */}
      <a href="#" data-click="scroll-top" className="btn-scroll-top fade">
        <i className="ti-arrow-up"></i>
      </a>
    </div>
  );
};

export default EditCustomPage;
